import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Stepper, Step, StepLabel, Button, Typography, Paper, List, ListItem, ListItemAvatar,
    Avatar, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, AlertTitle, Divider, Stack, Snackbar, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions
} from "@mui/material";
import { PlayArrow as PlayIcon, CheckCircleOutline as CheckIcon, Groups as GroupsIcon, HelpOutline as HelpIcon } from "@mui/icons-material";
import { getData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import Loader from "../../../../common/spinner";
import CompensationStepper from "./steppers/compensationStepper";
import LeaveAttendanceStepper from "./steppers/leaveAttendanceStepper";
import ITDeclarationsStepper from "./steppers/ITDeclarationsStepper";

const steps = [
    "Compensation",
    "Leave & Attendance",
    "IT Declarations",
    "Leave Encashment",
    "Reimbursements",
    "Banking and Statutory",
    "Review",
];

function ProcessFlow({ payPeriod }) {
    const [activeStep, setActiveStep] = useState(0);
    const [viewState, setViewState] = useState("initial");
    const [confirmedSteps, setConfirmedSteps] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [batchOverview, setBatchOverview] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const getShortMonth = (fullMonth) => {
        return new Date(`${fullMonth} 1, 2020`).toLocaleString("en-US", {
            month: "short",
        });
    };

    const fetchBatchData = useCallback(async () => {
        if (!payPeriod) return;
        setLoading(true);
        const [monthName, year] = payPeriod.split(" ");
        const shortMonth = getShortMonth(monthName);

        try {
            const res = await getData(API_ENDPOINTS.getBatchOverview(year, shortMonth));
            if (res?.statusCode === 200) {
                const pendingList = res.result?.filter(emp => emp.locked === false) || [];
                setBatchOverview(pendingList);
                if (viewState === "initial") setViewState("pending");
            }
        } catch (error) {
            setBatchOverview([]);
            setSnackbar({ open: true, message: "Error fetching batch data", severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }, [payPeriod, viewState]);

    useEffect(() => {
        if (viewState !== "initial") fetchBatchData();
    }, [activeStep, fetchBatchData, viewState]);

    const getStepContent = (step) => {
        switch (step) {
            case 0: return <CompensationStepper batchData={batchOverview} payPeriod={payPeriod} />;
            case 1: return <LeaveAttendanceStepper batchData={batchOverview} payPeriod={payPeriod} />;
            case 2: return <ITDeclarationsStepper batchData={batchOverview} payPeriod={payPeriod} />;
            default: return <Typography sx={{ p: 4, textAlign: 'center' }}>Stepper component for <b>{steps[step]}</b> is coming soon.</Typography>;
        }
    };

    const handleStartInitial = () => fetchBatchData();
    const handleProceedWithPending = () => { setViewState("processing"); setActiveStep(0); };
    const handleNext = () => {
        if (!confirmedSteps.has(activeStep)) {
            setSnackbar({ open: true, message: "Please confirm this step before continuing.", severity: "warning" });
            return;
        }
        setActiveStep((prev) => prev + 1);
    };
    const handleBack = () => {
        if (viewState === "processing") {
            if (activeStep === 0) setViewState("pending");
            else setActiveStep((prev) => prev - 1);
        } else if (viewState === "pending") {
            setViewState("initial");
        }
    };

    const handleConfirmFinal = () => {
        setConfirmedSteps((prev) => new Set(prev).add(activeStep));
        setConfirmDialogOpen(false);
        setSnackbar({
            open: true,
            message: `${steps[activeStep]} data has been verified and confirmed!`,
            severity: "success"
        });
    };

    const InitialFlowView = () => (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 4, bgcolor: '#fafafa', mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Ready to process payroll for {payPeriod}?</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Review and confirm employee data across the following checkpoints:
                </Typography>
                <Button variant="contained" size="large" startIcon={<PlayIcon />} onClick={handleStartInitial} sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}>
                    Start Payroll Process
                </Button>
            </Paper>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, px: 1 }}>Process Checkpoints</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={index}>
                            <ListItem sx={{ py: 1.5 }}>
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'transparent', color: "#4caf50", width: 32, height: 32, fontSize: '0.9rem', fontWeight: 'bold', border: "2px solid #4caf50" }}>
                                        {index + 1}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={step} primaryTypographyProps={{ fontWeight: 500 }} />
                            </ListItem>
                            {index < steps.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
        </Box>
    );

    const PendingEmployeesView = () => (
        <Box sx={{ mt: 2 }}>
            <Alert severity="warning" variant="outlined" icon={<GroupsIcon />} sx={{ borderRadius: 3, mb: 3, bgcolor: '#fffdf6' }}>
                <AlertTitle sx={{ fontWeight: 'bold' }}>Payroll pending for the following employees</AlertTitle>
                These records need to be processed to complete the {payPeriod} cycle.
            </Alert>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Employee ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batchOverview.map((emp) => (
                            <TableRow key={emp.empId} hover>
                                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>{emp.empId}</TableCell>
                                <TableCell>{emp.name}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                <Button variant="outlined" onClick={handleBack} sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}>Back</Button>
                <Button variant="contained" color="primary" size="large" onClick={handleProceedWithPending} sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}>
                    Proceed with Pending Employees
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ p: 1 }}>
            {loading && viewState === "initial" ? <Box sx={{ textAlign: 'center', py: 10 }}><Loader /></Box> : (
                <>
                    {viewState === "initial" && <InitialFlowView />}
                    {viewState === "pending" && <PendingEmployeesView />}
                    {viewState === "processing" && (
                        <Box>
                            {/* --- THE STYLED STEPPER --- */}
                            <Stepper
                                activeStep={activeStep}
                                alternativeLabel
                                sx={{
                                    mb: 5,
                                    // Style for the icons when COMPLETED
                                    '& .MuiStepIcon-root.Mui-completed': { color: 'success.main' },
                                    // Style for the connector lines when COMPLETED
                                    '& .MuiStepConnector-line': {
                                        transition: 'border-color 0.3s ease'
                                    },
                                    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                                        borderColor: 'success.main',
                                    },
                                    // Optional: Change active color to a darker blue or match success
                                    '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' }
                                }}
                            >
                                {steps.map((label, index) => {
                                    const stepProps = {};
                                    if (confirmedSteps.has(index)) {
                                        stepProps.completed = true;
                                    }
                                    return (
                                        <Step key={label} {...stepProps}>
                                            <StepLabel>{label}</StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>

                            {getStepContent(activeStep)}

                            <Box sx={{ display: "flex", justifyContent: "space-between", pt: 2, mt: 3, borderTop: '1px solid #e0e0e0' }}>
                                <Button onClick={handleBack} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Back</Button>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        startIcon={confirmedSteps.has(activeStep) ? <CheckIcon /> : null}
                                        onClick={() => setConfirmDialogOpen(true)}
                                        disabled={confirmedSteps.has(activeStep)}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                    >
                                        {confirmedSteps.has(activeStep) ? "Step Confirmed" : "Confirm Step"}
                                    </Button>
                                    {activeStep < steps.length - 1 && (
                                        <Button variant="contained" onClick={handleNext} sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}>
                                            Continue
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
                    <HelpIcon color="primary" /> Confirm {steps[activeStep]} Data?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you have reviewed the {steps[activeStep].toLowerCase()} data for all pending employees?
                        Once confirmed, this step will be marked as complete.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <Button onClick={() => setConfirmDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmFinal} variant="contained" color="success" sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>
                        Yes, Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default ProcessFlow;